import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getDashboardT } from '../locales/dashboard';
import {
  Package,
  Truck,
  Users,
  TrendingUp,
  MapPin,
  Clock,
  CheckCircle,
  MoreHorizontal,
  Maximize2,
  X,
  CalendarDays,
  ShoppingCart,
  DollarSign,
  RotateCcw,
} from 'lucide-react';
import MainService from '../services/MainService';
import {
  filterMenuItemsByAccess,
  getCachedAllowedPages,
  isPrivilegedRole,
  readUserInfo,
} from '../services/pageAccess';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Recharts / Leaflet lay out in LTR; under document `dir=rtl` ticks clip and bars misalign.
 * Isolate direction for chart SVG / map only (labels can still show Arabic text).
 */
function LtrChartIsolate({ children, className = '', style }) {
  return (
    <div
      dir="ltr"
      className={className}
      style={{ unicodeBidi: 'isolate', minWidth: 0, ...style }}
    >
      {children}
    </div>
  );
}

/** UAE map — order intensity heat (dummy counts per area). */
function UAEMonthlyOrdersMap({ allowScrollZoom = false, t }) {
  const heatPoints = useMemo(() => {
    const raw = [
      { lat: 25.2048, lng: 55.2708, w: 1, labelKey: 'map.dubai', count: '1,420' },
      { lat: 25.12, lng: 55.38, w: 0.62, labelKey: 'map.dubaiSouth', count: '880' },
      { lat: 24.4539, lng: 54.3773, w: 0.78, labelKey: 'map.abuDhabi', count: '1,100' },
      { lat: 24.52, lng: 54.52, w: 0.45, labelKey: 'map.abuDhabiEast', count: '640' },
      { lat: 25.3463, lng: 55.4209, w: 0.64, labelKey: 'map.sharjah', count: '920' },
      { lat: 25.4052, lng: 55.5136, w: 0.44, labelKey: 'map.ajman', count: '510' },
      { lat: 24.1302, lng: 55.8023, w: 0.52, labelKey: 'map.alAin', count: '720' },
      { lat: 25.1288, lng: 56.3264, w: 0.38, labelKey: 'map.fujairah', count: '410' },
      { lat: 25.6741, lng: 55.9804, w: 0.42, labelKey: 'map.rak', count: '480' },
      { lat: 25.5647, lng: 55.5548, w: 0.3, labelKey: 'map.uaq', count: '290' },
      { lat: 23.14, lng: 53.82, w: 0.22, labelKey: 'map.western', count: '220' },
    ];
    return raw.map((p) => ({
      ...p,
      label: t(p.labelKey),
      valueLine: `${t('map.ordersCount', { n: p.count })} · ${t('map.thisMonth')}`,
    }));
  }, [t]);

  return (
    <MapContainer
      center={[24.35, 54.75]}
      zoom={7}
      minZoom={6}
      maxZoom={11}
      maxBounds={[
        [22.35, 50.65],
        [27.55, 57.15],
      ]}
      maxBoundsViscosity={0.92}
      scrollWheelZoom={allowScrollZoom}
      className="z-0 h-full w-full"
      style={{ height: '100%', width: '100%' }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {heatPoints.map((p, i) => (
        <CircleMarker
          key={`${p.labelKey}-${i}`}
          center={[p.lat, p.lng]}
          radius={16 + p.w * 42}
          pathOptions={{
            fillColor: '#46955A',
            color: '#14532d',
            fillOpacity: 0.14 + p.w * 0.46,
            weight: 0.5,
            opacity: 0.9,
          }}
        >
          <LeafletTooltip direction="top" offset={[0, -6]} opacity={1}>
            <div className="text-xs leading-snug">
              <div className="font-semibold text-gray-900">{p.label}</div>
              <div className="text-gray-600">{p.valueLine}</div>
            </div>
          </LeafletTooltip>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}

const DELIVERY_STATUS_SEGMENTS = [
  { id: 'deliveryInProgress', apiKey: 'DeliveryInProgress', sliceColor: '#a3e635', dotColor: '#84cc16' },
  { id: 'delivered', apiKey: 'Delivered', sliceColor: '#166534', dotColor: '#166534' },
  { id: 'cancelled', apiKey: 'Cancelled', sliceColor: '#fecaca', dotColor: '#ef4444' },
  { id: 'returned', apiKey: 'Returned', sliceColor: '#fcd34d', dotColor: '#f59e0b' },
  { id: 'picked', apiKey: 'Picked', sliceColor: '#93c5fd', dotColor: '#3b82f6' },
  { id: 'pickupInProgress', apiKey: 'PickupInProgress', sliceColor: '#7dd3fc', dotColor: '#0ea5e9' },
  { id: 'pickupReturn', apiKey: 'PickupReturn', sliceColor: '#fdba74', dotColor: '#f97316' },
  { id: 'pickupCancelled', apiKey: 'PickupCancelled', sliceColor: '#fda4af', dotColor: '#f43f5e' },
];

function parseOrderDate(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && value.Year != null && value.Month != null && value.Day != null) {
    return new Date(value.Year, value.Month - 1, value.Day);
  }
  if (typeof value === 'string' || value instanceof Date) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function formatRelativeOrderTime(dateValue, t) {
  const date = parseOrderDate(dateValue);
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('recentOrders.justNow');
  if (mins < 60) return t('recentOrders.minutesAgo', { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('recentOrders.hoursAgo', { n: hours });
  const days = Math.floor(hours / 24);
  return t('recentOrders.daysAgo', { n: days });
}

function formatRecentOrderMeta(order) {
  const area =
    order.CustomerAddress?.Location?.Area?.Name ??
    order.CustomerAddress?.Location?.Name ??
    order.CustomerAddress?.Emirate?.Name ??
    '';
  const customer = order.Customer?.Name ?? '';
  return area || customer || '—';
}

function readDashboardStat(data, pascalKey) {
  if (!data) return 0;
  const camelKey = pascalKey.charAt(0).toLowerCase() + pascalKey.slice(1);
  const raw = data[pascalKey] ?? data[camelKey];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Latin / Arabic spellings — merge totals so Dubai + دبي + دبى show as one locale label & one bar. */
function mergeKeyForDeliveryArea(areaName, areaId) {
  const raw = String(areaName ?? '').trim();
  if (!raw) return `id:${areaId ?? ''}`;
  if (/^dubai$/i.test(raw)) return 'dubaiMerged';
  const nfc = raw.normalize('NFC');
  if (nfc === 'دبي' || nfc === 'دبى') return 'dubaiMerged';
  return `id:${areaId ?? ''}`;
}

function localizedMergedDubaiName(lang) {
  return lang === 'ar' ? 'دبي' : 'Dubai';
}

function DeliveryStatusContent({ expanded, donutData, chartData, total, t, loading, language }) {
  const donutSize = expanded ? 280 : 168;
  const innerR = expanded ? '56%' : '60%';
  const outerR = expanded ? '90%' : '90%';
  const truckClass = expanded ? 'h-10 w-10 sm:h-11 sm:w-11' : 'h-8 w-8';
  const statusText = expanded ? 'text-base' : 'text-sm';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-gray-500">
        {t('deliveryStatus.loading')}
      </div>
    );
  }

  const pieSlices = chartData ?? donutData.filter((row) => row.value > 0);
  const pieData =
    pieSlices.length > 0
      ? pieSlices
      : [{ id: 'empty', value: 1, sliceColor: '#e5e7eb', dotColor: '#d1d5db' }];

  const chartBlock = (
    <div
      className="relative mx-auto shrink-0"
      style={{ width: donutSize, height: donutSize, maxWidth: '100%' }}
    >
      <LtrChartIsolate className="h-full w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={innerR}
              outerRadius={outerR}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              {pieData.map((entry, index) => (
                <Cell key={`delivery-slice-${entry.id}-${index}`} fill={entry.sliceColor} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [`${value}`, '']} />
          </PieChart>
        </ResponsiveContainer>
      </LtrChartIsolate>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 px-3 text-center">
        <Truck className={`${truckClass} text-primary drop-shadow-sm`} strokeWidth={1.6} aria-hidden />
        <span className={`font-bold leading-tight text-gray-900 ${statusText}`}>
          {t('deliveryStatus.centerLabel')}
        </span>
      </div>
    </div>
  );

  const locale = language === 'ar' ? 'ar-AE' : 'en-US';
  const legendMid = Math.ceil(donutData.length / 2);
  const legendColA = donutData.slice(0, legendMid);
  const legendColB = donutData.slice(legendMid);

  const renderLegendItem = (row) => {
    const pct = total > 0 ? (row.value / total) * 100 : 0;
    const isDelivered = row.id === 'delivered';
    const label = t(`deliveryStatus.${row.id}`);
    return (
      <div key={row.id} className="flex min-w-0 items-center gap-1 text-xs leading-tight">
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: row.dotColor }}
        />
        <span
          className={`min-w-0 flex-1 truncate ${
            isDelivered ? 'font-semibold text-primary' : 'font-medium text-gray-700'
          }`}
        >
          {label}
        </span>
        <span className="shrink-0 font-bold tabular-nums text-gray-900">
          {row.value.toLocaleString(locale)}
        </span>
        <span className="w-8 shrink-0 text-right text-[10px] tabular-nums text-gray-400">
          {pct.toFixed(1)}%
        </span>
      </div>
    );
  };

  const listBlock = (
    <div className="grid w-full min-w-0 grid-cols-2 gap-x-3">
      <div className="flex min-w-0 flex-col gap-1.5">{legendColA.map(renderLegendItem)}</div>
      <div className="flex min-w-0 flex-col gap-1.5">{legendColB.map(renderLegendItem)}</div>
    </div>
  );

  if (expanded) {
    return (
      <div className="flex min-w-0 flex-col items-stretch gap-6 lg:flex-row lg:items-center lg:gap-8">
        {chartBlock}
        <div className="min-w-0 flex-1">{listBlock}</div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col items-stretch gap-3">
      <div className="flex justify-center shrink-0 py-1">{chartBlock}</div>
      <div className="min-w-0 w-full">{listBlock}</div>
    </div>
  );
}

function TopAreasBarCard({
  title,
  subtitle,
  data,
  chartHeight = 260,
  yAxisWidth = 118,
  language = 'en',
  loading = false,
  loadingMessage = 'Loading…',
  emptyMessage = 'No data',
  ordersLabel = 'Orders',
}) {
  const isAr = language === 'ar';
  const yw = isAr ? Math.max(yAxisWidth, 160) : yAxisWidth;
  const tickMax = isAr ? 34 : 14;
  const tickFormatter = (v) => {
    if (typeof v !== 'string') return v;
    return v.length > tickMax ? `${v.slice(0, tickMax)}…` : v;
  };

  return (
    <div className="min-w-0 h-full bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4 flex items-start gap-2 min-w-0">
        <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={2} aria-hidden />
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p> : null}
        </div>
      </div>
      <LtrChartIsolate className="w-full" style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 12, left: 4, bottom: 0 }}
            barCategoryGap={8}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ececec" />
            <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
            <YAxis
              type="category"
              dataKey="name"
              width={yw}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 10, fill: '#4b5563' }}
              interval={0}
              tickFormatter={tickFormatter}
            />
            <Tooltip
              formatter={(value) => [value, ordersLabel]}
              contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '12px' }}
            />
            <Bar dataKey="orders" fill="#46955A" barSize={12} radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </LtrChartIsolate>
    </div>
  );
}

function TopClientsChartContent({ data, expanded, t, language = 'en', loading = false }) {
  const h = expanded ? 520 : 280;
  const isAr = language === 'ar';
  const yw = isAr ? (expanded ? 260 : 165) : expanded ? 220 : 118;
  const maxLen = isAr ? (expanded ? 48 : 22) : expanded ? 40 : 15;
  const tickFormatter = (v) =>
    typeof v === 'string' && v.length > maxLen ? `${v.slice(0, maxLen)}…` : v;

  if (loading) {
    return (
      <div
        className="flex items-center justify-center text-sm text-gray-500"
        style={{ height: h }}
      >
        {t('topClients.loading')}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div
        className="flex items-center justify-center px-4 text-center text-sm text-gray-500"
        style={{ height: h }}
      >
        {t('topClients.empty')}
      </div>
    );
  }

  return (
    <LtrChartIsolate className="w-full" style={{ height: h }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: expanded ? 20 : 12, left: 4, bottom: 4 }}
          barCategoryGap={expanded ? 14 : 10}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#ececec" />
          <XAxis type="number" tick={{ fontSize: expanded ? 11 : 10 }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
          <YAxis
            type="category"
            dataKey="name"
            width={yw}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: expanded ? 12 : 10, fill: '#4b5563' }}
            interval={0}
            tickFormatter={tickFormatter}
          />
          <Tooltip
            formatter={(value) => [`${value}`, t('topClients.ordersAxis')]}
            labelFormatter={(label) => label}
            contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '12px' }}
          />
          <Bar
            dataKey="orders"
            name={t('topClients.ordersAxis')}
            fill="#46955A"
            barSize={expanded ? 18 : 14}
            radius={[0, 6, 6, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </LtrChartIsolate>
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [enlargedChart, setEnlargedChart] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [deliveryStatusStats, setDeliveryStatusStats] = useState(null);
  const [deliveryStatusLoading, setDeliveryStatusLoading] = useState(true);
  const [topClientsRaw, setTopClientsRaw] = useState([]);
  const [topClientsLoading, setTopClientsLoading] = useState(true);
  const [topAreasRaw, setTopAreasRaw] = useState([]);
  const [topAreasLoading, setTopAreasLoading] = useState(true);
  const [driversSummaryRaw, setDriversSummaryRaw] = useState([]);
  const [driversSummaryLoading, setDriversSummaryLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentOrdersLoading, setRecentOrdersLoading] = useState(true);
  const { language } = useLanguage();
  const t = useMemo(() => getDashboardT(language), [language]);

  const shortcuts = useMemo(() => {
    const user = readUserInfo();
    const allowedPages = isPrivilegedRole(user.Role) ? null : (getCachedAllowedPages() || []);
    const items = [
      {
        id: 'createOrder',
        path: '/home/orders/create-order',
        icon: ShoppingCart,
        label: t('shortcuts.createOrder'),
        accent: 'bg-emerald-50 text-emerald-700',
      },
      {
        id: 'deliveryAllocation',
        path: '/home/delivery/allocation',
        icon: Truck,
        label: t('shortcuts.deliveryAllocation'),
        accent: 'bg-blue-50 text-blue-700',
      },
      {
        id: 'createShipment',
        path: '/home/shipments/new-shipment',
        icon: Package,
        label: t('shortcuts.createShipment'),
        accent: 'bg-violet-50 text-violet-700',
      },
      {
        id: 'createPayment',
        path: '/home/payments/create-payment',
        icon: DollarSign,
        label: t('shortcuts.createPayment'),
        accent: 'bg-amber-50 text-amber-700',
      },
      {
        id: 'createReturn',
        path: '/home/returns/create-return',
        icon: RotateCcw,
        label: t('shortcuts.createReturn'),
        accent: 'bg-orange-50 text-orange-700',
      },
    ];
    return filterMenuItemsByAccess(items, allowedPages);
  }, [t]);

  const formatCount = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return '—';
    return num.toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US');
  };

  useEffect(() => {
    let cancelled = false;
    const loadDashboardStats = async () => {
      setStatsLoading(true);
      try {
        const data = await MainService.GetAdminDashboardData();
        if (!cancelled) setDashboardStats(data || null);
      } catch (err) {
        console.error('GetAdminDashboardData:', err);
        if (!cancelled) setDashboardStats(null);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    loadDashboardStats();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDeliveryStatus = async () => {
      setDeliveryStatusLoading(true);
      try {
        const data = await MainService.GetAdminDashboardDeliveryStatus();
        if (!cancelled) setDeliveryStatusStats(data || null);
      } catch (err) {
        console.error('GetAdminDashboardDeliveryStatus:', err);
        if (!cancelled) setDeliveryStatusStats(null);
      } finally {
        if (!cancelled) setDeliveryStatusLoading(false);
      }
    };
    loadDeliveryStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadTopClients = async () => {
      setTopClientsLoading(true);
      try {
        const data = await MainService.TopClientsMonthly();
        if (!cancelled) {
          setTopClientsRaw(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('TopClientsMonthly:', err);
        if (!cancelled) setTopClientsRaw([]);
      } finally {
        if (!cancelled) setTopClientsLoading(false);
      }
    };
    loadTopClients();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadTopAreas = async () => {
      setTopAreasLoading(true);
      try {
        const response = await MainService.TopDeliveryAreas();
        if (!cancelled) {
          const list = response?.Data ?? response?.data ?? response;
          setTopAreasRaw(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error('TopDeliveryAreas:', err);
        if (!cancelled) setTopAreasRaw([]);
      } finally {
        if (!cancelled) setTopAreasLoading(false);
      }
    };
    loadTopAreas();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDriversSummary = async () => {
      setDriversSummaryLoading(true);
      try {
        const data = await MainService.DriversSummary();
        if (!cancelled) {
          setDriversSummaryRaw(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('DriversSummary:', err);
        if (!cancelled) setDriversSummaryRaw([]);
      } finally {
        if (!cancelled) setDriversSummaryLoading(false);
      }
    };
    loadDriversSummary();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadRecentOrders = async () => {
      setRecentOrdersLoading(true);
      try {
        const response = await MainService.RecentOrders({});
        if (!cancelled) {
          const data = response?.Data ?? response?.data ?? [];
          setRecentOrders(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('RecentOrders:', err);
        if (!cancelled) setRecentOrders([]);
      } finally {
        if (!cancelled) setRecentOrdersLoading(false);
      }
    };
    loadRecentOrders();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayValue = (pascalKey) => {
    if (statsLoading) return '…';
    if (!dashboardStats) return '—';
    const camelKey = pascalKey.charAt(0).toLowerCase() + pascalKey.slice(1);
    const raw = dashboardStats[pascalKey] ?? dashboardStats[camelKey];
    return formatCount(raw);
  };

  // KPI cards — order matches GetAdminDashboardData
  const counters = useMemo(
    () => [
      {
        id: 'totalOrders',
        title: t('kpi.totalOrders'),
        value: displayValue('TotalOrders'),
        icon: Package,
      },
      {
        id: 'totalClients',
        title: t('kpi.totalClients'),
        value: displayValue('TotalClients'),
        icon: Users,
      },
      {
        id: 'monthlyOrders',
        title: t('kpi.monthlyOrders'),
        value: displayValue('MonthlyDelivered'),
        icon: TrendingUp,
      },
      {
        id: 'todaysOrders',
        title: t('kpi.todaysOrders'),
        value: displayValue('TodaysOrders'),
        icon: CalendarDays,
      },
      {
        id: 'pendingOrders',
        title: t('kpi.pendingOrders'),
        value: displayValue('PendingOrders'),
        icon: Clock,
      },
      {
        id: 'completedToday',
        title: t('kpi.completedToday'),
        value: displayValue('CompletedOrders'),
        icon: CheckCircle,
      },
    ],
    [t, dashboardStats, statsLoading, language]
  );

  const topClientsMonthly = useMemo(() => {
    const rows = topClientsRaw.map((row) => ({
      name: row.ClientName ?? row.clientName ?? '—',
      orders: Number(row.TotalOrders ?? row.totalOrders) || 0,
    }));
    // Recharts lists first category at bottom — reverse so highest client is on top
    return [...rows].reverse();
  }, [topClientsRaw]);

  const deliveryDonutData = useMemo(
    () =>
      DELIVERY_STATUS_SEGMENTS.map((seg) => ({
        id: seg.id,
        value: readDashboardStat(deliveryStatusStats, seg.apiKey),
        sliceColor: seg.sliceColor,
        dotColor: seg.dotColor,
      })),
    [deliveryStatusStats]
  );
  const deliveryChartData = useMemo(
    () => deliveryDonutData.filter((row) => row.value > 0),
    [deliveryDonutData]
  );
  const deliveryTotal = deliveryDonutData.reduce((s, d) => s + d.value, 0);

  const topAreasData = useMemo(() => {
    const byMerge = new Map();
    for (const row of topAreasRaw) {
      const areaId = row.AreaId ?? row.areaId;
      const rawName =
        row.AreaName ??
        row.areaName ??
        (areaId != null ? `#${areaId}` : '—');
      const orders = Number(row.TotalOrders ?? row.totalOrders) || 0;
      const key = mergeKeyForDeliveryArea(rawName, areaId);
      const displayName =
        key === 'dubaiMerged'
          ? localizedMergedDubaiName(language)
          : String(rawName).trim() || (areaId != null ? `#${areaId}` : '—');

      const prev = byMerge.get(key);
      if (!prev) {
        byMerge.set(key, { name: displayName, orders, firstAreaId: areaId });
      } else {
        prev.orders += orders;
      }
    }

    let list = [...byMerge.values()];
    const nameCount = list.reduce((acc, r) => {
      acc[r.name] = (acc[r.name] || 0) + 1;
      return acc;
    }, {});
    list = list.map((r) => ({
      name: nameCount[r.name] > 1 ? `${r.name} (#${r.firstAreaId ?? '?'})` : r.name,
      orders: r.orders,
    }));
    // Recharts: first datum is drawn toward the TOP of the Y categories; descending → Dubai / max first.
    return list.sort((a, b) => b.orders - a.orders);
  }, [topAreasRaw, language]);

  const driversSummaryList = useMemo(
    () =>
      driversSummaryRaw.map((row) => ({
        id: row.EmployeeId ?? row.employeeId,
        name: row.DriverName ?? row.driverName ?? '—',
        count: Number(row.TotalDeliveries ?? row.totalDeliveries) || 0,
      })),
    [driversSummaryRaw]
  );

  useEffect(() => {
    if (!enlargedChart) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setEnlargedChart(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [enlargedChart]);

  const enlargedTitles = useMemo(
    () => ({
      delivery: t('enlarged.delivery'),
      clients: t('enlarged.clients'),
      orders: t('enlarged.orders'),
    }),
    [t]
  );

  return (
    <div className="space-y-6">
      {/* KPI metric cards — single row (scroll on narrow screens) */}
      <div className="flex flex-nowrap gap-4 overflow-x-auto pb-1 -mx-1 px-1 lg:grid lg:grid-cols-6 lg:overflow-x-visible lg:pb-0 lg:mx-0 lg:px-0">
        {counters.map((counter) => {
          const Icon = counter.icon;
          return (
            <div
              key={counter.id}
              className="min-w-[158px] shrink-0 lg:min-w-0 bg-white rounded-2xl border border-gray-100 p-4 lg:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_-4px_rgba(0,0,0,0.08)] transition-shadow"
            >
              <div className="flex items-center gap-2.5 mb-4 lg:mb-5 min-w-0">
                <Icon className="w-5 h-5 shrink-0 text-primary" strokeWidth={2} aria-hidden />
                <span className="text-sm font-medium text-gray-700 leading-snug">{counter.title}</span>
              </div>
              <span className="text-3xl font-bold tracking-tight text-gray-900 tabular-nums">
                {counter.value}
              </span>
            </div>
          );
        })}
      </div>

      {/* Charts — three per row on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Delivery Status */}
        <div className="min-w-0 overflow-hidden bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 truncate">{t('deliveryStatus.title')}</h2>
              <p className="text-xs text-gray-500 mt-0.5">{t('deliveryStatus.subtitle')}</p>
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                onClick={() => setEnlargedChart('delivery')}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label={t('deliveryStatus.enlarge')}
              >
                <Maximize2 className="w-5 h-5" strokeWidth={2} />
              </button>
              <button
                type="button"
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label={t('moreOptions')}
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="min-w-0">
            <DeliveryStatusContent
              expanded={false}
              donutData={deliveryDonutData}
              chartData={deliveryChartData}
              total={deliveryTotal}
              t={t}
              loading={deliveryStatusLoading}
              language={language}
            />
          </div>
        </div>

        {/* Top Clients (Monthly) — horizontal bar chart */}
        <div className="min-w-0 bg-white rounded-2xl border border-gray-100 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          <div className="mb-4 flex items-start justify-between gap-2 min-w-0">
            <div className="flex items-center min-w-0 gap-2">
              <Users className="h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
              <div className="min-w-0">
                <h2 className="text-base font-bold text-gray-900 truncate">{t('topClients.title')}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{t('topClients.subtitle')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEnlargedChart('clients')}
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 shrink-0"
              aria-label={t('topClients.enlarge')}
            >
              <Maximize2 className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
          <TopClientsChartContent
            data={topClientsMonthly}
            expanded={false}
            t={t}
            language={language}
            loading={topClientsLoading}
          />
        </div>

        {/* Monthly Orders — UAE map (Leaflet) */}
        <div className="min-w-0 bg-white rounded-2xl border border-gray-100 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          <div className="mb-3 flex items-start justify-between gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <MapPin className="h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
              <div className="min-w-0">
                <h2 className="text-base font-bold text-gray-900 truncate">{t('monthlyOrders.title')}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{t('monthlyOrders.subtitle')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEnlargedChart('orders')}
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 shrink-0"
              aria-label={t('monthlyOrders.enlarge')}
            >
              <Maximize2 className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
          <div className="h-[280px] w-full overflow-hidden rounded-xl border border-gray-100 bg-emerald-50/40">
            <LtrChartIsolate className="h-full w-full">
              <UAEMonthlyOrdersMap allowScrollZoom={false} t={t} />
            </LtrChartIsolate>
          </div>
        </div>
      </div>

      {shortcuts.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('shortcuts.title')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {shortcuts.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/70 px-3 py-3 text-left hover:border-primary/30 hover:bg-white hover:shadow-[0_8px_24px_-4px_rgba(0,0,0,0.08)] transition-all"
                >
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${item.accent}`}>
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </span>
                  <span className="text-sm font-semibold text-gray-800 leading-snug">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
        <div className="min-w-0 flex flex-col">
          {topAreasLoading ? (
            <div className="min-w-0 h-full bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="mb-4 flex items-start gap-2 min-w-0">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={2} aria-hidden />
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">{t('topAreas.title')}</h2>
                  <p className="mt-0.5 text-xs text-gray-500">{t('topAreas.subtitle')}</p>
                </div>
              </div>
              <div className="flex h-[260px] items-center justify-center text-sm text-gray-500">
                {t('topAreas.loading')}
              </div>
            </div>
          ) : topAreasData.length === 0 ? (
            <div className="min-w-0 h-full bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="mb-4 flex items-start gap-2 min-w-0">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={2} aria-hidden />
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">{t('topAreas.title')}</h2>
                  <p className="mt-0.5 text-xs text-gray-500">{t('topAreas.subtitle')}</p>
                </div>
              </div>
              <div className="flex h-[260px] items-center justify-center px-4 text-center text-sm text-gray-500">
                {t('topAreas.empty')}
              </div>
            </div>
          ) : (
            <TopAreasBarCard
              title={t('topAreas.title')}
              subtitle={t('topAreas.subtitle')}
              data={topAreasData}
              chartHeight={260}
              yAxisWidth={88}
              language={language}
              ordersLabel={t('topAreas.ordersAxis')}
            />
          )}
        </div>
        <div className="min-w-0 flex flex-col">
          <div className="flex h-full min-h-0 flex-col bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="mb-4 shrink-0 text-lg font-semibold text-gray-900">{t('recentOrders.title')}</h2>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto lg:max-h-[340px]">
              {recentOrdersLoading ? (
                <p className="py-8 text-center text-sm text-gray-500">{t('recentOrders.loading')}</p>
              ) : recentOrders.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">{t('recentOrders.empty')}</p>
              ) : (
                recentOrders.map((order) => {
                  const status = order.Status;
                  const statusColor = status?.Color || '#374151';
                  const metaParts = [
                    formatRecentOrderMeta(order),
                    formatRelativeOrderTime(order.DeliveryDate ?? order.Date, t),
                  ].filter(Boolean);
                  return (
                    <button
                      key={order.Id}
                      type="button"
                      onClick={() => order.Id && navigate(`/home/orders/${order.Id}`)}
                      className="flex w-full items-center justify-between gap-2 rounded-lg bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100"
                    >
                      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                        <Package className="h-5 w-5 shrink-0 text-gray-400" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {order.VoucherNo || `#${order.Id}`}
                          </p>
                          <p className="truncate text-xs text-gray-500">{metaParts.join(' · ')}</p>
                        </div>
                      </div>
                      {status?.Name ? (
                        <span
                          className="shrink-0 rounded-full px-2 py-1 text-xs font-medium"
                          style={{
                            backgroundColor: `${statusColor}20`,
                            color: statusColor,
                          }}
                        >
                          {status.Name}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
        <div className="min-w-0 flex flex-col">
          <div className="flex h-full min-h-0 flex-col bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4 flex shrink-0 items-start gap-2 min-w-0">
              <Users className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={2} aria-hidden />
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold text-gray-900">{t('drivers.title')}</h2>
                <p className="mt-0.5 text-xs text-gray-500">{t('drivers.subtitle')}</p>
              </div>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto lg:max-h-[340px]">
              {driversSummaryLoading ? (
                <p className="py-8 text-center text-sm text-gray-500">{t('drivers.loading')}</p>
              ) : driversSummaryList.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">{t('drivers.empty')}</p>
              ) : (
                driversSummaryList.map((row) => (
                  <div
                    key={row.id ?? row.name}
                    className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5 sm:px-4 sm:py-3"
                  >
                    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-100 bg-white shadow-sm sm:h-10 sm:w-10">
                        <Truck className="h-4 w-4 text-primary sm:h-5 sm:w-5" strokeWidth={2} aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{row.name}</p>
                        <p className="text-xs text-gray-500">{t('drivers.deliveries')}</p>
                      </div>
                    </div>
                    <span className="shrink-0 text-lg font-bold tabular-nums text-gray-900 sm:text-xl">
                      {formatCount(row.count)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {enlargedChart && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="chart-enlarge-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label={t('modal.closeOverlay')}
            onClick={() => setEnlargedChart(null)}
          />
          <div className="relative z-10 flex max-h-[min(92vh,920px)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <h2 id="chart-enlarge-title" className="text-lg font-bold text-gray-900 truncate pr-2">
                {enlargedTitles[enlargedChart]}
              </h2>
              <button
                type="button"
                onClick={() => setEnlargedChart(null)}
                className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label={t('modal.close')}
              >
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-5 sm:p-6">
              {enlargedChart === 'delivery' && (
                <DeliveryStatusContent
                  expanded
                  donutData={deliveryDonutData}
                  chartData={deliveryChartData}
                  total={deliveryTotal}
                  t={t}
                  loading={deliveryStatusLoading}
                  language={language}
                />
              )}
              {enlargedChart === 'clients' && (
                <TopClientsChartContent
                  data={topClientsMonthly}
                  expanded
                  t={t}
                  language={language}
                  loading={topClientsLoading}
                />
              )}
              {enlargedChart === 'orders' && (
                <div className="h-[min(72vh,640px)] min-h-[360px] w-full overflow-hidden rounded-xl border border-gray-100 bg-emerald-50/40">
                  <LtrChartIsolate className="h-full w-full">
                    <UAEMonthlyOrdersMap allowScrollZoom t={t} />
                  </LtrChartIsolate>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

